import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertController, IonInput, LoadingController, ModalController } from '@ionic/angular';
import { ConnectionService } from 'src/app/core/connection/connection.service';
import { FileHelperService } from 'src/app/core/helpers/file-helper/file-helper.service';
import { LoggerService } from 'src/app/core/logger/logger.service';
import { AttachmentUploadResponse } from 'src/app/core/models/attachment.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';


@Component({
	selector: 'app-appointment-attachment-add',
	templateUrl: './appointment-attachment-add.component.html',
	styleUrls: ['./appointment-attachment-add.component.scss'],
})
export class AppointmentAttachmentAddComponent implements OnInit {

	@Input()
	appointmentId: number;

	pageTitle = 'Add Attachment';
	addAttachmentForm: FormGroup;
	isFormValid = true;
	isUploading = false;
	private VALID_EXTENSIONS = ['.doc', '.docx', '.xls', '.xlsx', '.pdf', '.jpg', '.png', '.jpeg'];

	@ViewChild('filenameInputEl', { static: false })
	filenameInputEl: IonInput;

	@ViewChild('fileInputEl', { static: false })
	fileInputEl: IonInput;

	constructor(
		private modalController: ModalController,
		private formBuilder: FormBuilder,
		private fileHelper: FileHelperService,
		private appointmentService: AppointmentService,
		private logger: LoggerService,
		private loadingController: LoadingController,
		private alertController: AlertController,
		private connectionService: ConnectionService,
	) { }

	get validExtensions() {
		return this.VALID_EXTENSIONS.join(',');
	}

	get filename() {
		return (this.addAttachmentForm.controls.attachment.value as File)?.name;
	}

	ngOnInit() {
		this.addAttachmentForm = this.formBuilder.group({
			attachment: [(null as File), Validators.required],
			filename: [{ value: '', disabled: true }, Validators.required],
		});
	}

	async onClickTriggerNativeInput(): Promise<void> {
		const input = await this.fileInputEl.getInputElement();
		input.click();
	}

	async onAddAttachment(): Promise<void> {
		this.isFormValid = true;
		if (!this.addAttachmentForm.valid) {
			this.isFormValid = false;
			return;
		}

		if (!this.connectionService.isOnline$.value) {
			await this.alertOffline();
			return;
		}

		const attachment = this.addAttachmentForm.controls.attachment.value as File;
		const ext = this.fileHelper.getExtension(attachment.name) as string;
		if (!this.VALID_EXTENSIONS.includes(ext.toLowerCase())) {
			this.resetForm();
			await this.alertInvalidExtension();
			return;
		}

		const filename = this.addAttachmentForm.controls.filename.value;
		const formData = this.fileHelper.convertToFormData(attachment, `${filename + ext}`);

		const uploadPrompt = await this.presentUploadPrompt();
		this.appointmentService.addAttachment(this.appointmentId, formData)
			.subscribe(
				async (response: AttachmentUploadResponse) => {
					await uploadPrompt.dismiss();
					await this.dismiss(response);
				},
				async error => {
					this.logger.logException(error);
					await uploadPrompt.dismiss();
					await this.alertUploadFailed();
				}
			);
	}

	async alertInvalidExtension() {
		const alert = await this.alertController.create({
			header: 'Invalid File Extension',
			message: `File upload only supports the following file extensions: (${this.VALID_EXTENSIONS.join(', ')})`,
			buttons: ['OK'],
		});

		await alert.present();
	}

	async alertOffline(): Promise<void> {
		const alert = await this.alertController.create({
			header: 'Offline',
			message: 'Uploads cannot occur while offline.  Please try again later.',
			buttons: ['OK'],
		});

		await alert.present();
	}

	async presentUploadPrompt(): Promise<HTMLIonLoadingElement> {
		const prompt = await this.loadingController.create({
			message: 'Uploading...'
		});
		await prompt.present();

		return prompt;
	}

	async alertUploadFailed(): Promise<void> {
		const alert = await this.alertController.create({
			header: 'Upload Failed',
			message: 'There was an error and the upload could not complete.',
			buttons: ['OK'],
		});

		await alert.present();
	}

	async attachFile(files: FileList): Promise<void> {
		const file = files.item(0) as File;
		if (!file) {
			this.resetForm();
			return;
		}

		this.addAttachmentForm.controls.attachment.setValue(file);
		this.addAttachmentForm.controls.filename.setValue(this.fileHelper.removeExtension(file.name));
		this.addAttachmentForm.controls.filename.enable();
		await this.focusFilename();
	}

	async focusFilename(): Promise<void> {
		const el = await this.filenameInputEl.getInputElement();
		el.focus();
	}

	resetForm() {
		this.addAttachmentForm.reset();
		this.fileInputEl.value = null;
		this.addAttachmentForm.controls.filename.disable();
	}

	async dismiss(response?: AttachmentUploadResponse): Promise<void> {
		await this.modalController.dismiss({
			dismissed: true,
			attachmentUpload: response,
		});
	}

}
